/**
 * HomePage Utility Functions
 * Contains all business logic and data transformation functions for the homepage
 */

import { GREETINGS, TIME_BOUNDARIES } from "./constants";
import logger from "../../logger";

/**
 * Determines appropriate greeting message based on current time
 * @returns {string} Greeting message in Hebrew
 */
export const getTimeBasedGreeting = () => {
  const currentHour = new Date().getHours();

  if (currentHour < TIME_BOUNDARIES.AFTERNOON_START) {
    return GREETINGS.MORNING;
  }

  if (currentHour < TIME_BOUNDARIES.EVENING_START) {
    return GREETINGS.AFTERNOON;
  }

  return GREETINGS.EVENING;
};

/**
 * Filters external apps based on user's group membership
 * Admins have access to all apps
 * @param {Object} user - User object containing groups and permissions
 * @param {Array} appsConfig - Array of app configuration objects
 * @returns {Array} Filtered array of apps user has access to
 */
export const filterAppsByUserAccess = (user, appsConfig) => {
  if (!user?.groups || !user?.hasGroup) {
    return [];
  }

  if (user.isAdmin) {
    return appsConfig;
  }

  return appsConfig.filter((app) => user.hasGroup(app.requiredGroupId));
};

/**
 * Builds effective access rights object for user
 * Admins get all permissions enabled
 * @param {Object} accessRights - User's access rights object
 * @param {boolean} isAdmin - Whether user is admin
 * @returns {Object} Effective access rights object
 */
export const buildEffectiveAccessRights = (accessRights, isAdmin) => {
  if (!accessRights) {
    return {};
  }

  if (isAdmin) {
    return Object.fromEntries(
      Object.keys(accessRights).map((key) => [key, true])
    );
  }

  return accessRights;
};

/**
 * Maps dashboard cards with their dynamic data (counts, loading states)
 * @param {Array} cards - Array of dashboard card configurations
 * @param {Object} pageData - Current page data with counts
 * @param {Object} loadingStates - Current loading states
 * @returns {Array} Cards with enriched data
 */
export const enrichCardsWithData = (cards, pageData, loadingStates, errors = {}) => {
  return cards.map((card) => {
    const cardData = getCardData(card.accessRight, pageData, loadingStates, errors);

    return {
      ...card,
      ...cardData,
      // Keep isExternal from original card, don't override
    };
  });
};

/**
 * Gets count, loading and error state for a specific card type
 * @param {string} accessRight - Access right identifier
 * @param {Object} pageData - Page data containing counts
 * @param {Object} loadingStates - Loading states object
 * @param {Object} errors - Per-section fetch error flags
 * @returns {Object} Object with count, loading and error state
 */
const getCardData = (accessRight, pageData, loadingStates, errors) => {
  switch (accessRight) {
    case "AproveLinesPage":
      return {
        count: pageData.approveLines,
        loading: loadingStates.approveLines,
        error: errors.approveLines,
      };
    case "ObligoExceptionPage":
      return {
        count: pageData.obligoExceptions,
        loading: loadingStates.obligoExceptions,
        error: errors.obligoExceptions,
      };
    default:
      return {
        count: "",
        loading: false,
      };
  }
};

/**
 * Navigates to external application URL
 * @param {string} url - External application URL
 */
export const navigateToExternalApp = (url) => {
  if (!url) {
    logger.error("navigateToExternalApp called with invalid URL", { url });
    return;
  }

  window.location.href = url;
};

/**
 * Handles user logout process
 * Clears local storage and redirects to logout
 * @param {Object} instance - MSAL instance
 * @param {Function} setUser - Function to update user state
 * @param {Function} setAllBranches - Function to update branches state
 * @param {Function} setTransactionsType - Function to update transactions state
 */
export const handleUserLogout = (
  instance,
  setUser,
  setAllBranches,
  setTransactionsType
) => {
  try {
    localStorage.removeItem("userContextData");
    localStorage.removeItem("qrCodeScannedItems"); // Clear QR code scanned items
    setUser({});
    setAllBranches([]);
    setTransactionsType([]);

    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
      account: instance.getActiveAccount(),
    });
  } catch (error) {
    logger.error("Error during logout process", { error: error.message });
  }
};

/**
 * Checks if user should see obligo section
 * @param {Object} accessRights - User's access rights
 * @returns {boolean} Whether user can view obligo section
 */
export const canViewObligoSection = (accessRights) => {
  return !!(accessRights?.ObligoExceptionPage || accessRights?.ViewObligoData);
};
